import { Component, OnInit } from '@angular/core';

interface PricingPlan {
  name: string;
  ram: string;
  vcpu: number;
  storage: string;
  discount: string;
  hourlyPrice: number;
  monthlyPrice: number;
  yearlyPrice: number;
  minimumBilling?: number;
}

interface StorageOption {
  size: string;
  price: number;
  pricePerGB?: number;
  transfer?: string;
}

interface DatabaseOption {
  instanceType: string;
  node: string;
  ram: string;
  storage: string;
  hourlyPrice: number;
  monthlyPrice: number;
  yearlyPrice: number;
}

interface FAQ {
  question: string;
  answer: string;
}

@Component({
  selector: 'app-iis-pricing',
  templateUrl: './iis-pricing.component.html',
  styleUrls: ['./iis-pricing.component.scss']
})
export class IisPricingComponent implements OnInit {
  // Active category filter
  activeCategory: string | null = null;

  filterVcpuExact: number | null = null;
  filterRamExactGB: number | null = null;

  // Operating System selection for Compute plans
  sharedCpuOS: 'windows' | 'linux' = 'linux';
  dedicatedCpuOS: 'windows' | 'linux' = 'linux';
  highMemoryOS: 'windows' | 'linux' = 'linux';
  loadBalancerOS: 'windows' | 'linux' = 'linux';

  // Billing cycles
  sharedCpuBilling: 'hourly' | 'monthly' | 'yearly' = 'monthly';
  dedicatedCpuBilling: 'hourly' | 'monthly' | 'yearly' = 'monthly';
  highMemoryBilling: 'hourly' | 'monthly' | 'yearly' = 'monthly';
  loadBalancerBilling: 'hourly' | 'monthly' | 'yearly' = 'monthly';
  mongoDbBilling: 'hourly' | 'monthly' | 'yearly' = 'monthly';
  mySqlBilling: 'hourly' | 'monthly' | 'yearly' = 'monthly';
  postgreSqlBilling: 'hourly' | 'monthly' | 'yearly' = 'monthly';
  backupBilling: 'hourly' | 'monthly' | 'yearly' = 'monthly';

  // LoadBalancer instance type
  loadBalancerInstanceType: 'Shared CPU' | 'Dedicated CPU' | 'High Memory' = 'Shared CPU';
  
  // Database instance types
  mongoDbInstanceType: 'Shared CPU' | 'Dedicated CPU' | 'High Memory' = 'Shared CPU';
  mySqlInstanceType: 'Shared CPU' | 'Dedicated CPU' | 'High Memory' = 'Shared CPU';
  postgreSqlInstanceType: 'Shared CPU' | 'Dedicated CPU' | 'High Memory' = 'Shared CPU';

  // Node quantity for MySQL (newly added)
  mySqlNodeQuantity: number = 1;

  // Block Storage calculator
  blockStorageSize: number = 50; // Default to 50 GB
  blockStoragePricePerGB: number = 5;

  // Archive Storage calculator
  archiveStorageSize: number = 50; // Default to 50 GB
  archiveStoragePricePerGB: number = 3; // ₹3 per GB

  // Expanded FAQ index
  expandedFaq: number | null = null;

  getFeaturedPlans(): PricingPlan[] {
    const source = this.sharedCpuOS === 'linux' ? this.sharedCpuPlansLinux : this.sharedCpuPlansWindows;
    return this.applyPlanFilters(source).slice(0, 3);
  }

  getFeaturedPrice(plan: PricingPlan): number {
    if (this.sharedCpuBilling === 'hourly') return plan.hourlyPrice;
    if (this.sharedCpuBilling === 'monthly') return plan.monthlyPrice;
    return plan.yearlyPrice;
  }

  private parseRamGB(ram: string): number {
    const m = ram.match(/(\d+)\s*GB/i);
    return m ? parseInt(m[1], 10) : 0;
  }

  private applyPlanFilters(plans: PricingPlan[]): PricingPlan[] {
    let filtered = plans;
    if (this.filterVcpuExact != null) {
      filtered = filtered.filter(p => p.vcpu === (this.filterVcpuExact as number));
    }
    if (this.filterRamExactGB != null) {
      filtered = filtered.filter(p => this.parseRamGB(p.ram) === (this.filterRamExactGB as number));
    }
    return filtered;
  }


  // Shared CPU Plans - Linux
  sharedCpuPlansLinux: PricingPlan[] = [
    {
      name: 'Insight Starter',
      ram: '4 GB',
      vcpu: 2,
      storage: '80 GB SSD',
      discount: '0%',
      hourlyPrice: 2.08,
      monthlyPrice: 1495,
      yearlyPrice: 14352
    },
    {
      name: 'Insight Professional',
      ram: '8 GB',
      vcpu: 4,
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 4.16,
      monthlyPrice: 2995,
      yearlyPrice: 28752
    },
    {
      name: 'Insight Business',
      ram: '16 GB',
      vcpu: 6,
      storage: '320 GB SSD',
      discount: '0%',
      hourlyPrice: 6.38,
      monthlyPrice: 4595,
      yearlyPrice: 44112
    },
    {
      name: 'Insight Enterprise',
      ram: '32 GB',
      vcpu: 8,
      storage: '480 GB SSD',
      discount: '0%',
      hourlyPrice: 10.41,
      monthlyPrice: 7495,
      yearlyPrice: 71952
    },
    {
      name: 'Insight Elite',
      ram: '64 GB',
      vcpu: 12,
      storage: '640 GB SSD',
      discount: '0%',
      hourlyPrice: 18.05,
      monthlyPrice: 12995,
      yearlyPrice: 124752
    },
    {
      name: 'Insight Premium',
      ram: '96 GB',
      vcpu: 24,
      storage: '840 GB SSD',
      discount: '0%',
      hourlyPrice: 34.72,
      monthlyPrice: 24995,
      yearlyPrice: 239952
    },
    {
      name: 'Insight Advanced',
      ram: '128 GB',
      vcpu: 32,
      storage: '960 GB SSD',
      discount: '0%',
      hourlyPrice: 45.83,
      monthlyPrice: 32995,
      yearlyPrice: 316752
    },
    {
      name: 'Insight Ultimate',
      ram: '192 GB',
      vcpu: 48,
      storage: '1280 GB SSD',
      discount: '0%',
      hourlyPrice: 63.88,
      monthlyPrice: 45995,
      yearlyPrice: 441552
    }
  ];

  // Shared CPU Plans - Windows (with higher pricing)
  sharedCpuPlansWindows: PricingPlan[] = [
    {
      name: 'Insight Starter Windows',
      ram: '20 GB',
      vcpu: 2,
      storage: '200 GB SSD',
      discount: '15%',
      hourlyPrice: 6.73,
      monthlyPrice: 4846,
      yearlyPrice: 41201,
      minimumBilling: 1000
    },
    {
      name: 'Insight Professional Windows',
      ram: '40 GB',
      vcpu: 4,
      storage: '400 GB SSD',
      discount: '15%',
      hourlyPrice: 10.1,
      monthlyPrice: 7272,
      yearlyPrice: 61812,
      minimumBilling: 2000
    },
    {
      name: 'Insight Business Windows',
      ram: '60 GB',
      vcpu: 6,
      storage: '600 GB SSD',
      discount: '15%',
      hourlyPrice: 13.46,
      monthlyPrice: 9690,
      yearlyPrice: 82365,
      minimumBilling: 3000
    },
    {
      name: 'Insight Enterprise Windows',
      ram: '80 GB',
      vcpu: 8,
      storage: '800 GB SSD',
      discount: '15%',
      hourlyPrice: 17.95,
      monthlyPrice: 12921,
      yearlyPrice: 109829,
      minimumBilling: 4000
    },
    {
      name: 'Insight Elite Windows',
      ram: '100 GB',
      vcpu: 10,
      storage: '1000 GB SSD',
      discount: '15%',
      hourlyPrice: 21.93,
      monthlyPrice: 15780,
      yearlyPrice: 134130,
      minimumBilling: 5000
    },
    {
      name: 'Insight Premium Windows',
      ram: '120 GB',
      vcpu: 12,
      storage: '1200 GB SSD',
      discount: '15%',
      hourlyPrice: 26.93,
      monthlyPrice: 19380,
      yearlyPrice: 164730,
      minimumBilling: 6000
    },
    {
      name: 'Insight Advanced Windows',
      ram: '140 GB',
      vcpu: 14,
      storage: '1400 GB SSD',
      discount: '15%',
      hourlyPrice: 30.5,
      monthlyPrice: 21950,
      yearlyPrice: 186575,
      minimumBilling: 7000
    },
    {
      name: 'Insight Ultimate Windows',
      ram: '160 GB',
      vcpu: 16,
      storage: '1600 GB SSD',
      discount: '15%',
      hourlyPrice: 35.9,
      monthlyPrice: 25840,
      yearlyPrice: 219640,
      minimumBilling: 8000
    },
  ];

  // Dedicated CPU Plans - Linux
  dedicatedCpuPlansLinux: PricingPlan[] = [
    {
      name: 'Insight Power Starter',
      ram: '4 GB',
      vcpu: 2,
      storage: '80 GB SSD',
      discount: '0%',
      hourlyPrice: 3.19,
      monthlyPrice: 2295,
      yearlyPrice: 22032
    },
    {
      name: 'Insight Power Pro',
      ram: '8 GB',
      vcpu: 4,
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 6.38,
      monthlyPrice: 4595,
      yearlyPrice: 44112
    },
    {
      name: 'Insight Power Business',
      ram: '32 GB',
      vcpu: 8,
      storage: '480 GB SSD',
      discount: '0%',
      hourlyPrice: 18.05,
      monthlyPrice: 12995,
      yearlyPrice: 124752
    },
    {
      name: 'Insight Power Enterprise',
      ram: '64 GB',
      vcpu: 16,
      storage: '960 GB SSD',
      discount: '0%',
      hourlyPrice: 34.72,
      monthlyPrice: 24995,
      yearlyPrice: 239952
    },
    {
      name: 'Insight Power Elite',
      ram: '96 GB',
      vcpu: 32,
      storage: '1280 GB SSD',
      discount: '0%',
      hourlyPrice: 62.63,
      monthlyPrice: 45095,
      yearlyPrice: 432912
    },
    {
      name: 'Insight Power Ultimate',
      ram: '128 GB',
      vcpu: 48,
      storage: '1980 GB SSD',
      discount: '0%',
      hourlyPrice: 90.41,
      monthlyPrice: 65095,
      yearlyPrice: 624912
    }
  ];

  // Dedicated CPU Plans - Windows (with higher pricing)
  dedicatedCpuPlansWindows: PricingPlan[] = [
    {
      name: 'Insight Power Starter Windows',
      ram: '20 GB',
      vcpu: 2,
      storage: '200 GB SSD',
      discount: '10%',
      hourlyPrice: 14,
      monthlyPrice: 10080,
      yearlyPrice: 90720,
      minimumBilling: 4500
    },
    {
      name: 'Insight Power Pro Windows',
      ram: '40 GB',
      vcpu: 4,
      storage: '400 GB SSD',
      discount: '10%',
      hourlyPrice: 26,
      monthlyPrice: 18720,
      yearlyPrice: 168480,
      minimumBilling: 6500
    },
    {
      name: 'Insight Power Business Windows',
      ram: '80 GB',
      vcpu: 8,
      storage: '800 GB SSD',
      discount: '10%',
      hourlyPrice: 52,
      monthlyPrice: 37440,
      yearlyPrice: 336960,
      minimumBilling: 10400
    },
    {
      name: 'Insight Power Enterprise Windows',
      ram: '160 GB',
      vcpu: 16,
      storage: '1600 GB SSD',
      discount: '10%',
      hourlyPrice: 106,
      monthlyPrice: 76320,
      yearlyPrice: 686880,
      minimumBilling: 20800
    },
    {
      name: 'Insight Power Elite Windows',
      ram: '320 GB',
      vcpu: 32,
      storage: '3200 GB SSD',
      discount: '10%',
      hourlyPrice: 209,
      monthlyPrice: 150480,
      yearlyPrice: 1354320,
      minimumBilling: 41600
    },
    {
      name: 'Insight Power Ultimate Windows',
      ram: '480 GB',
      vcpu: 48,
      storage: '4800 GB SSD',
      discount: '10%',
      hourlyPrice: 314,
      monthlyPrice: 226080,
      yearlyPrice: 2034720,
      minimumBilling: 62400
    },
  ];

  // High Memory Plans - Linux
  highMemoryPlansLinux: PricingPlan[] = [
    {
      name: 'Insight RAM Starter',
      ram: '8 GB',
      vcpu: 1,
      storage: '120 GB SSD',
      discount: '0%',
      hourlyPrice: 2.22,
      monthlyPrice: 1595,
      yearlyPrice: 15312
    },
    {
      name: 'Insight RAM Pro',
      ram: '16 GB',
      vcpu: 2,
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 3.96,
      monthlyPrice: 2850,
      yearlyPrice: 27360
    },
    {
      name: 'Insight RAM Business',
      ram: '32 GB',
      vcpu: 4,
      storage: '320 GB SSD',
      discount: '0%',
      hourlyPrice: 7.92,
      monthlyPrice: 5700,
      yearlyPrice: 54720
    },
    {
      name: 'Insight RAM Enterprise',
      ram: '64 GB',
      vcpu: 8,
      storage: '640 GB SSD',
      discount: '0%',
      hourlyPrice: 15.83,
      monthlyPrice: 11400,
      yearlyPrice: 109440
    },
    {
      name: 'Insight RAM Elite',
      ram: '64 GB',
      vcpu: 8,
      storage: '960 GB SSD',
      discount: '0%',
      hourlyPrice: 17.61,
      monthlyPrice: 12680,
      yearlyPrice: 121728
    },
    {
      name: 'Insight RAM Ultimate',
      ram: '128 GB',
      vcpu: 16,
      storage: '1295 GB SSD',
      discount: '0%',
      hourlyPrice: 40.27,
      monthlyPrice: 28995,
      yearlyPrice: 278352
    }
  ];

  // High Memory Plans - Windows (with higher pricing)
  highMemoryPlansWindows: PricingPlan[] = [
    {
      name: 'Insight RAM Starter Windows',
      ram: '24 GB',
      vcpu: 2,
      storage: '200 GB SSD',
      discount: '10%',
      hourlyPrice: 18,
      monthlyPrice: 12960,
      yearlyPrice: 116640,
      minimumBilling: 5500
    },
    {
      name: 'Insight RAM Pro Windows',
      ram: '48 GB',
      vcpu: 4,
      storage: '400 GB SSD',
      discount: '10%',
      hourlyPrice: 30,
      monthlyPrice: 21600,
      yearlyPrice: 194400,
      minimumBilling: 8500
    },
    {
      name: 'Insight RAM Business Windows',
      ram: '72 GB',
      vcpu: 6,
      storage: '600 GB SSD',
      discount: '10%',
      hourlyPrice: 42,
      monthlyPrice: 30240,
      yearlyPrice: 272160,
      minimumBilling: 11500
    },
    {
      name: 'Insight RAM Enterprise Windows',
      ram: '96 GB',
      vcpu: 8,
      storage: '800 GB SSD',
      discount: '10%',
      hourlyPrice: 54,
      monthlyPrice: 38880,
      yearlyPrice: 349920,
      minimumBilling: 14500
    },
    {
      name: 'Insight RAM Elite Windows',
      ram: '120 GB',
      vcpu: 10,
      storage: '1000 GB SSD',
      discount: '10%',
      hourlyPrice: 66,
      monthlyPrice: 47520,
      yearlyPrice: 427680,
      minimumBilling: 17500
    },
    {
      name: 'Insight RAM Ultimate Windows',
      ram: '144 GB',
      vcpu: 12,
      storage: '1200 GB SSD',
      discount: '10%',
      hourlyPrice: 78,
      monthlyPrice: 56160,
      yearlyPrice: 505440,
      minimumBilling: 20500
    },
  ];

  // Object Storage Options
  objectStorageOptions: StorageOption[] = [
    {
      size: '100 GB - 1 TB',
      price: 400,
      pricePerGB: 4,
      transfer: '100 GB'
    },
    {
      size: '1 TB - 5 TB',
      price: 3500,
      pricePerGB: 3.5,
      transfer: '500 GB'
    },
    {
      size: '5 TB - 10 TB',
      price: 15000,
      pricePerGB: 3,
      transfer: '2 TB'
    },
    {
      size: '10 TB+',
      price: 25000,
      pricePerGB: 2.5,
      transfer: '5 TB'
    }
  ];

  // Database Options - Pre-calculated to avoid infinite loops
  // Current database options for display
  mongoDbOptions: DatabaseOption[] = [];
  mySqlOptions: DatabaseOption[] = [];
  postgreSqlOptions: DatabaseOption[] = [];
  
  // MongoDB Options
  mongoDbSharedCpuOptions: DatabaseOption[] = [
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '4 GB',
      storage: '80 GB',
      hourlyPrice: 3.74,
      monthlyPrice: 2691,
      yearlyPrice: 32292
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '8 GB',
      storage: '160 GB',
      hourlyPrice: 7.49,
      monthlyPrice: 5391,
      yearlyPrice: 64692
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '16 GB',
      storage: '320 GB',
      hourlyPrice: 11.49,
      monthlyPrice: 8271,
      yearlyPrice: 99252
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '32 GB',
      storage: '480 GB',
      hourlyPrice: 18.74,
      monthlyPrice: 13491,
      yearlyPrice: 161892
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '64 GB',
      storage: '640 GB',
      hourlyPrice: 32.49,
      monthlyPrice: 23391,
      yearlyPrice: 280692
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '96 GB',
      storage: '840 GB',
      hourlyPrice: 62.49,
      monthlyPrice: 44991,
      yearlyPrice: 539892
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '128 GB',
      storage: '960 GB',
      hourlyPrice: 82.49,
      monthlyPrice: 59391,
      yearlyPrice: 712692
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '192 GB',
      storage: '1280 GB',
      hourlyPrice: 114.99,
      monthlyPrice: 82791,
      yearlyPrice: 993492
    }
  ];
  
  mongoDbDedicatedCpuOptions: DatabaseOption[] = [
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '4 GB',
      storage: '80 GB',
      hourlyPrice: 5.74,
      monthlyPrice: 4131,
      yearlyPrice: 49572
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '8 GB',
      storage: '160 GB',
      hourlyPrice: 11.49,
      monthlyPrice: 8271,
      yearlyPrice: 99252
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '32 GB',
      storage: '480 GB',
      hourlyPrice: 32.49,
      monthlyPrice: 23391,
      yearlyPrice: 280692
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '64 GB',
      storage: '960 GB',
      hourlyPrice: 62.49,
      monthlyPrice: 44991,
      yearlyPrice: 539892
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '96 GB',
      storage: '1280 GB',
      hourlyPrice: 112.74,
      monthlyPrice: 81171,
      yearlyPrice: 974052
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '128 GB',
      storage: '1980 GB',
      hourlyPrice: 162.74,
      monthlyPrice: 117171,
      yearlyPrice: 1406052
    }
  ];
  
  mongoDbHighMemoryOptions: DatabaseOption[] = [
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '16 GB',
      storage: '160 GB',
      hourlyPrice: 7.13,
      monthlyPrice: 5130,
      yearlyPrice: 61560
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '32 GB',
      storage: '320 GB',
      hourlyPrice: 14.25,
      monthlyPrice: 10260,
      yearlyPrice: 123120
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '64 GB',
      storage: '640 GB',
      hourlyPrice: 28.50,
      monthlyPrice: 20520,
      yearlyPrice: 246240
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '64 GB',
      storage: '960 GB',
      hourlyPrice: 31.70,
      monthlyPrice: 22824,
      yearlyPrice: 273888
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '128 GB',
      storage: '1295 GB',
      hourlyPrice: 72.49,
      monthlyPrice: 52191,
      yearlyPrice: 626292
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '256 GB',
      storage: '1980 GB',
      hourlyPrice: 164.99,
      monthlyPrice: 118791,
      yearlyPrice: 1425492
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '384 GB',
      storage: '2490 GB',
      hourlyPrice: 187.49,
      monthlyPrice: 134991,
      yearlyPrice: 1619892
    }
  ];
  
  // MySQL Options
  mySqlSharedCpuOptions: DatabaseOption[] = [
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '4 GB',
      storage: '80 GB',
      hourlyPrice: 3.74,
      monthlyPrice: 2691,
      yearlyPrice: 32292
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '8 GB',
      storage: '160 GB',
      hourlyPrice: 7.49,
      monthlyPrice: 5391,
      yearlyPrice: 64692
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '16 GB',
      storage: '320 GB',
      hourlyPrice: 11.49,
      monthlyPrice: 8271,
      yearlyPrice: 99252
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '32 GB',
      storage: '480 GB',
      hourlyPrice: 18.74,
      monthlyPrice: 13491,
      yearlyPrice: 161892
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '64 GB',
      storage: '640 GB',
      hourlyPrice: 32.49,
      monthlyPrice: 23391,
      yearlyPrice: 280692
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '96 GB',
      storage: '840 GB',
      hourlyPrice: 62.49,
      monthlyPrice: 44991,
      yearlyPrice: 539892
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '128 GB',
      storage: '960 GB',
      hourlyPrice: 82.49,
      monthlyPrice: 59391,
      yearlyPrice: 712692
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '192 GB',
      storage: '1280 GB',
      hourlyPrice: 114.99,
      monthlyPrice: 82791,
      yearlyPrice: 993492
    }
  ];
  
  mySqlDedicatedCpuOptions: DatabaseOption[] = [
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '4 GB',
      storage: '80 GB',
      hourlyPrice: 5.74,
      monthlyPrice: 4131,
      yearlyPrice: 49572
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '8 GB',
      storage: '160 GB',
      hourlyPrice: 11.49,
      monthlyPrice: 8271,
      yearlyPrice: 99252
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '32 GB',
      storage: '480 GB',
      hourlyPrice: 32.49,
      monthlyPrice: 23391,
      yearlyPrice: 280692
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '64 GB',
      storage: '960 GB',
      hourlyPrice: 62.49,
      monthlyPrice: 44991,
      yearlyPrice: 539892
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '96 GB',
      storage: '1280 GB',
      hourlyPrice: 112.74,
      monthlyPrice: 81171,
      yearlyPrice: 974052
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '128 GB',
      storage: '1980 GB',
      hourlyPrice: 162.74,
      monthlyPrice: 117171,
      yearlyPrice: 1406052
    }
  ];
  
  mySqlHighMemoryOptions: DatabaseOption[] = [
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '16 GB',
      storage: '160 GB',
      hourlyPrice: 7.13,
      monthlyPrice: 5130,
      yearlyPrice: 61560
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '32 GB',
      storage: '320 GB',
      hourlyPrice: 14.25,
      monthlyPrice: 10260,
      yearlyPrice: 123120
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '64 GB',
      storage: '640 GB',
      hourlyPrice: 28.50,
      monthlyPrice: 20520,
      yearlyPrice: 246240
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '64 GB',
      storage: '960 GB',
      hourlyPrice: 31.70,
      monthlyPrice: 22824,
      yearlyPrice: 273888
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '128 GB',
      storage: '1295 GB',
      hourlyPrice: 72.49,
      monthlyPrice: 52191,
      yearlyPrice: 626292
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '256 GB',
      storage: '1980 GB',
      hourlyPrice: 164.99,
      monthlyPrice: 118791,
      yearlyPrice: 1425492
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '384 GB',
      storage: '2490 GB',
      hourlyPrice: 187.49,
      monthlyPrice: 134991,
      yearlyPrice: 1619892
    }
  ];
  
  // PostgreSQL Options
  postgreSqlSharedCpuOptions: DatabaseOption[] = [
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '4 GB',
      storage: '80 GB',
      hourlyPrice: 3.74,
      monthlyPrice: 2691,
      yearlyPrice: 32292
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '8 GB',
      storage: '160 GB',
      hourlyPrice: 7.49,
      monthlyPrice: 5391,
      yearlyPrice: 64692
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '16 GB',
      storage: '320 GB',
      hourlyPrice: 11.49,
      monthlyPrice: 8271,
      yearlyPrice: 99252
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '32 GB',
      storage: '480 GB',
      hourlyPrice: 18.74,
      monthlyPrice: 13491,
      yearlyPrice: 161892
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '64 GB',
      storage: '640 GB',
      hourlyPrice: 32.49,
      monthlyPrice: 23391,
      yearlyPrice: 280692
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '96 GB',
      storage: '840 GB',
      hourlyPrice: 62.49,
      monthlyPrice: 44991,
      yearlyPrice: 539892
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '128 GB',
      storage: '960 GB',
      hourlyPrice: 82.49,
      monthlyPrice: 59391,
      yearlyPrice: 712692
    },
    {
      instanceType: 'Shared CPU',
      node: 'Single Node',
      ram: '192 GB',
      storage: '1280 GB',
      hourlyPrice: 114.99,
      monthlyPrice: 82791,
      yearlyPrice: 993492
    }
  ];
  
  postgreSqlDedicatedCpuOptions: DatabaseOption[] = [
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '4 GB',
      storage: '80 GB',
      hourlyPrice: 5.74,
      monthlyPrice: 4131,
      yearlyPrice: 49572
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '8 GB',
      storage: '160 GB',
      hourlyPrice: 11.49,
      monthlyPrice: 8271,
      yearlyPrice: 99252
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '32 GB',
      storage: '480 GB',
      hourlyPrice: 32.49,
      monthlyPrice: 23391,
      yearlyPrice: 280692
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '64 GB',
      storage: '960 GB',
      hourlyPrice: 62.49,
      monthlyPrice: 44991,
      yearlyPrice: 539892
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '96 GB',
      storage: '1280 GB',
      hourlyPrice: 112.74,
      monthlyPrice: 81171,
      yearlyPrice: 974052
    },
    {
      instanceType: 'Dedicated CPU',
      node: 'Single Node',
      ram: '128 GB',
      storage: '1980 GB',
      hourlyPrice: 162.74,
      monthlyPrice: 117171,
      yearlyPrice: 1406052
    }
  ];
  
  postgreSqlHighMemoryOptions: DatabaseOption[] = [
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '16 GB',
      storage: '160 GB',
      hourlyPrice: 7.13,
      monthlyPrice: 5130,
      yearlyPrice: 61560
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '32 GB',
      storage: '320 GB',
      hourlyPrice: 14.25,
      monthlyPrice: 10260,
      yearlyPrice: 123120
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '64 GB',
      storage: '640 GB',
      hourlyPrice: 28.50,
      monthlyPrice: 20520,
      yearlyPrice: 246240
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '64 GB',
      storage: '960 GB',
      hourlyPrice: 31.70,
      monthlyPrice: 22824,
      yearlyPrice: 273888
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '128 GB',
      storage: '1295 GB',
      hourlyPrice: 72.49,
      monthlyPrice: 52191,
      yearlyPrice: 626292
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '256 GB',
      storage: '1980 GB',
      hourlyPrice: 164.99,
      monthlyPrice: 118791,
      yearlyPrice: 1425492
    },
    {
      instanceType: 'High Memory',
      node: 'Single Node',
      ram: '384 GB',
      storage: '2490 GB',
      hourlyPrice: 187.49,
      monthlyPrice: 134991,
      yearlyPrice: 1619892
    }
  ];

  // Frequently Asked Questions
  faqs: FAQ[] = [
    {
      question: 'How am I billed for cloud services?',
      answer: 'You are billed hourly for compute resources based on the configuration you select. Storage and other services are billed monthly. You can choose to pay monthly or annually with a 20% discount for annual billing.'
    },
    {
      question: 'Can I upgrade or downgrade my plan?',
      answer: 'Yes, you can easily upgrade or downgrade your plan at any time through the client portal. Changes take effect immediately and you are billed pro-rated for the remainder of the billing period.'
    },
    {
      question: 'Do you offer any discounts for long-term commitments?',
      answer: 'Yes, we offer a 20% discount when you choose annual billing for any service. Additionally, volume discounts are available for customers with multiple instances or large storage requirements.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards including Visa, Mastercard, and American Express. We also support bank transfers and can accommodate other payment methods for enterprise customers.'
    },
    {
      question: 'Is there a minimum contract period?',
      answer: 'No, there is no minimum contract period. All our services are pay-as-you-go with no long-term commitments required. You can cancel at any time with no penalties.'
    },
    {
      question: 'Do you offer free trials or credits for new customers?',
      answer: 'Yes, we offer new customers a ₹1000 credit to try our services. Additionally, many of our services include a 30-day free trial period for new instances.'
    }
  ];

  constructor() { }

  ngOnInit(): void {
    // Initialize with MongoDB options
    this.mongoDbOptions = this.mongoDbSharedCpuOptions;
    this.mySqlOptions = this.mySqlSharedCpuOptions;
    this.postgreSqlOptions = this.postgreSqlSharedCpuOptions;
    
    // Initialize the selected database options based on the current instance type
    this.updateMongoDbOptions();
    this.updateMySqlOptions();
    this.updatePostgreSqlOptions();
  }

  // Method to check if a category is visible
  isCategoryVisible(category: string): boolean {
    return this.activeCategory === null || this.activeCategory === category;
  }

  // Method to set active category
  setActiveCategory(category: string | null): void {
    this.activeCategory = category;
  }

  // Methods to set OS for different compute plans
  setSharedCpuOS(os: 'windows' | 'linux'): void {
    this.sharedCpuOS = os;
  }

  setDedicatedCpuOS(os: 'windows' | 'linux'): void {
    this.dedicatedCpuOS = os;
  }

  setHighMemoryOS(os: 'windows' | 'linux'): void {
    this.highMemoryOS = os;
  }

  setLoadBalancerOS(os: 'windows' | 'linux'): void {
    this.loadBalancerOS = os;
  }

  // Methods to set billing cycles for different plans
  setSharedCpuBilling(billing: 'hourly' | 'monthly' | 'yearly'): void {
    this.sharedCpuBilling = billing;
  }

  setDedicatedCpuBilling(billing: 'hourly' | 'monthly' | 'yearly'): void {
    this.dedicatedCpuBilling = billing;
  }

  setHighMemoryBilling(billing: 'hourly' | 'monthly' | 'yearly'): void {
    this.highMemoryBilling = billing;
  }

  setLoadBalancerBilling(billing: 'hourly' | 'monthly' | 'yearly'): void {
    this.loadBalancerBilling = billing;
  }

  setMongoDbBilling(billing: 'hourly' | 'monthly' | 'yearly'): void {
    this.mongoDbBilling = billing;
    this.updateMongoDbOptions();
  }

  setMySqlBilling(billing: 'hourly' | 'monthly' | 'yearly'): void {
    this.mySqlBilling = billing;
    this.updateMySqlOptions();
  }

  setPostgreSqlBilling(billing: 'hourly' | 'monthly' | 'yearly'): void {
    this.postgreSqlBilling = billing;
    this.updatePostgreSqlOptions();
  }

  setBackupBilling(billing: 'hourly' | 'monthly' | 'yearly'): void {
    this.backupBilling = billing;
  }



  // Methods to set instance types for databases
  setMongoDbInstanceType(instanceType: 'Shared CPU' | 'Dedicated CPU' | 'High Memory'): void {
    this.mongoDbInstanceType = instanceType;
    this.updateMongoDbOptions();
  }

  setMySqlInstanceType(instanceType: 'Shared CPU' | 'Dedicated CPU' | 'High Memory'): void {
    this.mySqlInstanceType = instanceType;
    this.updateMySqlOptions();
  }

  setPostgreSqlInstanceType(instanceType: 'Shared CPU' | 'Dedicated CPU' | 'High Memory'): void {
    this.postgreSqlInstanceType = instanceType;
    this.updatePostgreSqlOptions();
  }

  // Getter methods for compute plans based on OS selection
  get sharedCpuPlans(): PricingPlan[] {
    const filtersActive = (this.filterVcpuExact != null) || (this.filterRamExactGB != null);
    const base = filtersActive
      ? [...this.sharedCpuPlansLinux, ...this.sharedCpuPlansWindows]
      : (this.sharedCpuOS === 'linux' ? this.sharedCpuPlansLinux : this.sharedCpuPlansWindows);
    return this.applyPlanFilters(base);
  }

  get dedicatedCpuPlans(): PricingPlan[] {
    const filtersActive = (this.filterVcpuExact != null) || (this.filterRamExactGB != null);
    const base = filtersActive
      ? [...this.dedicatedCpuPlansLinux, ...this.dedicatedCpuPlansWindows]
      : (this.dedicatedCpuOS === 'linux' ? this.dedicatedCpuPlansLinux : this.dedicatedCpuPlansWindows);
    return this.applyPlanFilters(base);
  }

  get highMemoryPlans(): PricingPlan[] {
    const filtersActive = (this.filterVcpuExact != null) || (this.filterRamExactGB != null);
    const base = filtersActive
      ? [...this.highMemoryPlansLinux, ...this.highMemoryPlansWindows]
      : (this.highMemoryOS === 'linux' ? this.highMemoryPlansLinux : this.highMemoryPlansWindows);
    return this.applyPlanFilters(base);
  }

  // Method to get discount display
  getDiscountDisplay(billing: string): string {
    if (billing !== 'yearly') {
      return '';
    }
    
    // Check which category is active and return appropriate discount
    if (this.activeCategory === 'compute') {
      // Check which OS is selected for compute plans
      if (this.sharedCpuOS === 'windows') {
        return '15% OFF';
      } else if (this.dedicatedCpuOS === 'windows' || this.highMemoryOS === 'windows') {
        return '10% OFF';
      }
    }
    
    // Default for Linux plans and other cases
    return '20% OFF';
  }

  // Method to toggle FAQ expansion
  toggleFaq(index: number): void {
    this.expandedFaq = this.expandedFaq === index ? null : index;
  }

  // Database engine selection
  selectedDatabaseEngine: 'MongoDB' | 'MySQL' | 'PostgreSQL' = 'MongoDB';

  // Update methods for database options to avoid infinite loops
  private updateMongoDbOptions(): void {
    // In a real implementation, this would calculate different options based on the selected instance type and billing
    // For now, we'll just update the instanceType property to match the current selection
    if (this.mongoDbInstanceType === 'Shared CPU') {
      this.mongoDbOptions = this.mongoDbSharedCpuOptions;
    } else if (this.mongoDbInstanceType === 'Dedicated CPU') {
      this.mongoDbOptions = this.mongoDbDedicatedCpuOptions;
    } else if (this.mongoDbInstanceType === 'High Memory') {
      this.mongoDbOptions = this.mongoDbHighMemoryOptions;
    }
  }

  private updateMySqlOptions(): void {
    // In a real implementation, this would calculate different options based on the selected instance type and billing
    // For now, we'll just update the instanceType property to match the current selection
    if (this.mySqlInstanceType === 'Shared CPU') {
      this.mySqlOptions = this.mySqlSharedCpuOptions;
    } else if (this.mySqlInstanceType === 'Dedicated CPU') {
      this.mySqlOptions = this.mySqlDedicatedCpuOptions;
    } else if (this.mySqlInstanceType === 'High Memory') {
      this.mySqlOptions = this.mySqlHighMemoryOptions;
    }
  }

  private updatePostgreSqlOptions(): void {
    // In a real implementation, this would calculate different options based on the selected instance type and billing
    // For now, we'll just update the instanceType property to match the current selection
    if (this.postgreSqlInstanceType === 'Shared CPU') {
      this.postgreSqlOptions = this.postgreSqlSharedCpuOptions;
    } else if (this.postgreSqlInstanceType === 'Dedicated CPU') {
      this.postgreSqlOptions = this.postgreSqlDedicatedCpuOptions;
    } else if (this.postgreSqlInstanceType === 'High Memory') {
      this.postgreSqlOptions = this.postgreSqlHighMemoryOptions;
    }
  }

  // Properties instead of methods to avoid infinite loops in template
  get currentMongoDbOptions(): DatabaseOption[] {
    return this.mongoDbOptions;
  }

  get currentMySqlOptions(): DatabaseOption[] {
    return this.mySqlOptions;
  }

  get currentPostgreSqlOptions(): DatabaseOption[] {
    return this.postgreSqlOptions;
  }
}
